import { useTranslations } from '@vocab/react';
import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useFormContext } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import { type Resume, VirusScanStatus } from 'src/__generated__/graphql';
import { MAX_DOCUMENTS } from 'src/constants/app';
import { useConfig } from 'src/context/config';
import { Page, PageSection, useTrackingClient } from 'src/context/tracking';
import { uploadResume } from 'src/context/upload-resume';
import {
  AttachmentOptionCombobox,
  AttachmentOptionFile,
  AttachmentSelect,
  FileAttachment,
} from 'src/forms/common/AttachmentSelect/AttachmentSelect';
import { useValidators } from 'src/forms/common/AttachmentSelect/hooks/useValidators';
import { useVirusScan } from 'src/hooks/useVirusScan';
import { useDialogRouterState } from 'src/modules/use-dialog-router-state';
import { DocumentLimitExceededDialog } from 'src/pages/Documents/components/DocumentLimitExceededDialog/DocumentLimitExceededDialog';

import translations from './.vocab';
import { useTracking } from './hooks/useTracking';

export interface ResumeFieldsValues {
  'resume-method': FileAttachment;
  'resume-file': File;
  'resume-select': string;
  'resume-uri': string;
  'resume-virusScan': unknown;
}

interface Props {
  hideTitle?: boolean;
  resumes: Resume[];
  defaultValue?: Resume;
  defaultMethod?: FileAttachment;
}

export function ResumeFields({
  resumes,
  defaultValue,
  hideTitle,
  defaultMethod,
}: Props) {
  const { config } = useConfig();
  const { t } = useTranslations(translations);
  const { trackProfileUpdate } = useTracking();
  const [isLoading, setIsLoading] = useState(false);
  const client = useTrackingClient();
  const { watch, setValue, trigger, formState, setError, clearErrors } =
    useFormContext<ResumeFieldsValues>();

  const hasResumeVirus = resumes.some(
    (resume) => resume?.fileMetadata?.virusScanStatus === 'HasVirus',
  );
  const hasSpi = resumes.some(
    (resume) => resume?.fileMetadata?.sensitiveDataInfo?.isDetected === true,
  );

  useEffect(() => {
    const baseTrackingValues = {
      link: 'alert_viewed',
      eventName: 'alert_viewed',
      currentPage: Page.DOCUMENT_PAGE,
      currentPageSection: PageSection.DOCUMENTS,
      alertShownType: 'notice',
      alertShownPosition: 'content',
    };

    if (hasResumeVirus) {
      client.trackLink({
        ...baseTrackingValues,
        alertShownTone: 'critical',
        alertShownTitle: 'resume with virus detected',
        alertShownMessage: 'Virus detected. Resume cant be used.',
      });
    }

    if (hasSpi) {
      client.trackLink({
        ...baseTrackingValues,
        alertShownTone: 'caution',
        alertShownTitle: 'resume with sensitive information',
        alertShownMessage: 'May contain sensitive information',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { validateMethod, validateFileSelection, validateFile } = useValidators(
    { currentMethod: watch('resume-method') || defaultMethod },
  );

  useVirusScan({
    fileUri: watch('resume-uri'),
    currentMethod: watch('resume-method'),
    onStatusChange: (status, method) => {
      const shouldCheck =
        method === FileAttachment.UPLOAD || method === FileAttachment.CHANGE;

      if (!shouldCheck) {
        clearErrors('resume-virusScan');
      } else if (status === VirusScanStatus.HasVirus) {
        setError('resume-virusScan', { message: t('Virus Detected') });
      } else {
        clearErrors('resume-virusScan');
      }

      setIsLoading(status === VirusScanStatus.Pending);
    },
  });

  const {
    isOpen: documentLimitDialogOpen,
    open: openDocumentLimitDialog,
    close: closeDocumentLimitDialog,
  } = useDialogRouterState(`document-limit-resume`);

  const handleResumeUpload = async (resume?: File) => {
    if (!resume) return;

    try {
      setIsLoading(true);
      const result = await uploadResume({
        resume,
        guid: uuid(),
        zone: config.ZONE,
      });
      trackProfileUpdate();
      flushSync(() => {
        setValue('resume-uri', result.uri);
        setValue('resume-select', result.resume.id);
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      // handle error
    } finally {
      trigger('resume-file'); // trigger validation
    }
  };

  const handleResumeSelect = (id?: string) => {
    if (!id) return;
    const selected = resumes.find((r) => r.id === id);
    if (!selected) return;

    flushSync(() => setValue('resume-uri', selected?.fileMetadata.uri));
    trigger('resume-select');
  };

  return (
    <>
      <AttachmentSelect
        hideTitle={hideTitle}
        title={t('Resume')}
        name="resume-method"
        defaultValue={defaultMethod}
        labelNone={t('Dont include a resume')}
        validate={validateMethod}
      >
        <AttachmentOptionFile
          testid="resumeFileInput"
          name="resume-file"
          label={t('Upload a resume')}
          description={t('Accepted file types')}
          isLoading={isLoading}
          reUploadText={t('Upload a different resume')}
          onClick={(e) => {
            if (resumes.length >= MAX_DOCUMENTS) {
              e.preventDefault();
              openDocumentLimitDialog();
            }
          }}
          onChange={handleResumeUpload}
          validate={validateFile}
          message={formState.errors['resume-virusScan']?.message}
        />
        <AttachmentOptionCombobox
          testid="resumeSelectInput"
          name="resume-select"
          label={t('Select a resume')}
          resumes={resumes}
          defaultValue={defaultValue?.id || null}
          onChange={handleResumeSelect}
          validate={validateFileSelection}
          message={formState.errors['resume-virusScan']?.message}
        />
      </AttachmentSelect>
      <DocumentLimitExceededDialog
        open={documentLimitDialogOpen}
        onClose={closeDocumentLimitDialog}
        documentList={resumes}
        onDeleted={closeDocumentLimitDialog}
      />
    </>
  );
}
